/**
 * Whole-table revisions authored "mytender.io".
 *
 * Word's document model gave the agent a table as a run of empty paragraphs,
 * and deleting those "applied" without touching the table (PE-604 testing).
 * A table is now one unit: its OOXML is read back from Word, every row, run
 * and paragraph mark is stamped as a deletion by mytender.io, and the result
 * replaces the table in place, so the user accepts or rejects the whole
 * table like any other revision. Pure string transforms; no Office.js here.
 */
import { REVISION_AUTHOR } from "./authoredRevision";

export type TableOoxmlInfo = {
  /** The table carries gridSpan/vMerge cells, which `table.values` cannot round-trip. */
  mergedCells: boolean;
  /** Authors of the revisions already inside the table, de-duplicated. */
  revisionAuthors: string[];
  /** Every revision inside the table is a mytender.io insertion. */
  onlyOurInsertions: boolean;
};

const TABLE = /<w:tbl[\s>][\s\S]*<\/w:tbl>/;

const tableMarkup = (pkg: string): string | null => {
  const match = TABLE.exec(pkg);
  return match ? match[0] : null;
};

/**
 * What the table's OOXML says about merges and existing revisions.
 * @param pkg - The package `Range.getOoxml()` returned for the table.
 */
export const inspectTableOoxml = (pkg: string): TableOoxmlInfo => {
  const tbl = tableMarkup(pkg) ?? "";
  const authors = new Set<string>();
  let onlyOurInsertions = true;
  const marks = /<w:(ins|del|moveFrom|moveTo)\b[^>]*?w:author="([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = marks.exec(tbl)) !== null) {
    const author = match[2].replace(/&amp;/g, "&").replace(/&quot;/g, '"');
    authors.add(author);
    if (match[1] !== "ins" || author !== REVISION_AUTHOR) onlyOurInsertions = false;
  }
  if (!authors.size) onlyOurInsertions = false;
  return {
    mergedCells: /<w:(gridSpan|vMerge)\b/.test(tbl),
    revisionAuthors: [...authors],
    onlyOurInsertions,
  };
};

let revisionId = 5000;
const attrs = (date: string) => `w:id="${revisionId++}" w:author="${REVISION_AUTHOR}" w:date="${date}"`;

/**
 * The same package with the table marked deleted, row by row and run by run.
 * @param pkg - The package `Range.getOoxml()` returned for the table.
 * @returns The package to hand back to `insertOoxml(..., "Replace")`.
 */
export const stampTableDeleted = (pkg: string): string => {
  const tbl = tableMarkup(pkg);
  if (!tbl) throw new Error("The OOXML holds no table.");
  const date = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
  let out = tbl;
  // Rows: a deletion mark in each row's properties.
  out = out.replace(
    /<w:tr(\s[^>]*)?>([\s\S]*?)(?=<w:tc|<\/w:tr>)/g,
    (whole, rowAttrs: string | undefined, head: string) => {
      const open = `<w:tr${rowAttrs ?? ""}>`;
      if (/<w:trPr\b/.test(head)) return whole.replace(/<w:trPr(\s[^>]*)?>/, (m) => `${m}<w:del ${attrs(date)}/>`);
      return `${open}<w:trPr><w:del ${attrs(date)}/></w:trPr>${head}`;
    }
  );
  // Runs: wrapped in a deletion, their text as deleted text.
  out = out.replace(/<w:r(\s[^>]*)?>([\s\S]*?)<\/w:r>/g, (_whole, runAttrs: string | undefined, inner: string) => {
    const text = inner.replace(/<w:t(\s[^>]*)?>/g, "<w:delText$1>").replace(/<\/w:t>/g, "</w:delText>");
    return `<w:del ${attrs(date)}><w:r${runAttrs ?? ""}>${text}</w:r></w:del>`;
  });
  // Paragraph marks inside the cells.
  out = out.replace(
    /<w:p(\s[^>]*)?>([\s\S]*?)(?=<w:r\b|<w:del\b|<w:ins\b|<w:hyperlink\b|<w:fldSimple\b|<\/w:p>)/g,
    (whole, pAttrs: string | undefined, head: string) => {
      if (/<w:pPr\b/.test(head)) {
        if (/<w:rPr\b/.test(head)) return whole.replace(/<w:rPr(\s[^>]*)?>/, (m) => `${m}<w:del ${attrs(date)}/>`);
        return whole.replace(/<\/w:pPr>/, `<w:rPr><w:del ${attrs(date)}/></w:rPr></w:pPr>`);
      }
      return `<w:p${pAttrs ?? ""}><w:pPr><w:rPr><w:del ${attrs(date)}/></w:rPr></w:pPr>${head}`;
    }
  );
  return pkg.replace(tbl, out);
};
